import MenuItem from '@mui/material/MenuItem';
import {
  GridColDef,
  useGridApiContext,
  gridVisibleColumnFieldsSelector,
  gridVisibleColumnDefinitionsSelector,
  gridFilterModelSelector,
  gridSortModelSelector,
} from '@mui/x-data-grid-pro';
import type { IColumn } from 'json-as-xlsx';
import React from 'react';

import { ExcelExportMenuItemProps } from './ExcelExportMenuItem.props';
import { ListType } from '../../../@types';
import {
  EXPORT_EMAILED_FALLBACK,
  EXPORT_STATUS,
  EXPORT_TOAST_KEY,
} from '../../../hooks/useListExport';
import { useExportFile } from '../../../queries/uploadedfiles-query';
import { getSelectedColumnNames } from '../../../utils/columnSelectionOperations';
import { downloadFile } from '../../../utils/file-utils';
import { toast } from '../../../utils/toast';

export const ExcelExportMenuItem: React.FC<ExcelExportMenuItemProps> = ({
  label,
  fileName,
  fileType,
  structureTypeCategory,
  columnSelectionData,
  sortModel,
  startDate,
  endDate,
  serialSearch,
  locationId,
  includeChilds,
  setExporting,
  rowCount,
  defaultExcludedColumns,
  serialPartsManifestData,
  nonSerialPartsManifestData,
  pageSize,
  columnOrder,
  replenishmentFilterData,
  defaultReport,
  multiSearchQuery,
  obfuscation,
}) => {
  const apiRef = useGridApiContext();
  const exportFile = useExportFile();

  const getExcludedColumns = () => {
    if (fileName === ListType.StructureList) {
      return getSelectedColumnNames(columnSelectionData, []);
    } else if (fileName === ListType.ClientContractList) {
      return getSelectedColumnNames(columnSelectionData, defaultExcludedColumns);
    } else if (fileName === ListType.PartList) {
      return getSelectedColumnNames(columnSelectionData);
    }
  };

  const downloadExcelFile = async () => {
    const visibleColumnsField = gridVisibleColumnFieldsSelector(apiRef);
    const visibleColumns = gridVisibleColumnDefinitionsSelector(apiRef);
    const filter = replenishmentFilterData || gridFilterModelSelector(apiRef);
    const sorting = gridSortModelSelector(apiRef);

    const columns = visibleColumns.map((obj: GridColDef) => {
      return { label: obj.headerName, value: obj.field } as IColumn;
    });

    setExporting(true);
    toast.info(EXPORT_STATUS.inProgress.message, { dedupeKey: EXPORT_TOAST_KEY });
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; dynamic payload whose shape varies per fileName; typing the union is a separate cleanup.
      const data: any = {
        selectedColumns: columns,
        filter: filter,
        fileName: fileName,
        visibleColumnsField: visibleColumnsField,
        fileType: fileType,
        structureTypeCategory: structureTypeCategory,
        columnSelectionData: columnSelectionData,
        sorting: sorting.length ? sorting : sortModel,
        startDate,
        endDate,
        serialSearch,
        locationId,
        includeChilds,
        rowCount,
        serialPartsManifestData,
        nonSerialPartsManifestData,
        pageSize,
        columnOrder,
        defaultReport,
        multiSearchQuery,
        obfuscation,
      };
      if (
        fileName === ListType.StructureList ||
        fileName === ListType.ClientContractList ||
        fileName === ListType.PartList
      ) {
        data.excludedColumns = getExcludedColumns();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; mutation response isn't typed yet.
      const exportFileLink: any = await exportFile.mutateAsync(data);
      if (exportFileLink?.sendEmail) {
        toast.info(exportFileLink?.message || EXPORT_EMAILED_FALLBACK, {
          dedupeKey: EXPORT_TOAST_KEY,
        });
        return;
      }
      downloadFile(exportFileLink.url, fileName);
      toast.success(EXPORT_STATUS.success.message, { dedupeKey: EXPORT_TOAST_KEY });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast.error(EXPORT_STATUS.failed.message, { dedupeKey: EXPORT_TOAST_KEY });
    } finally {
      setExporting(false);
    }
  };

  return <MenuItem onClick={downloadExcelFile}>{label}</MenuItem>;
};
